import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import useEscapeToGoBack from "../hooks/useEscapeToGoBack";
import "./DaySummary.css";

const API = "https://www.lianfoong.com/api/pawn-transactions/cashflow";

export default function DaySummary() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);

  const businessDate = useMemo(() => {
    const stored = localStorage.getItem("customDate");
    return stored ? new Date(stored) : new Date();
  }, []);

  useEscapeToGoBack();

  const yyyyMmDd = useMemo(() => {
    const d = businessDate;
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }, [businessDate]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}?date=${yyyyMmDd}`);
        if (!res.ok) throw new Error(await res.text());
        const json = await res.json();
        setData(json);
      } catch (e) {
        setErr(e.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [yyyyMmDd, setData]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && navigate("/dashboard");
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  if (loading) return <div className="ds-container"><div className="ds-card">Loading…</div></div>;
  if (err)
    return (
      <div className="ds-container">
        <div className="ds-card">
          <div className="ds-title">Day Cashflow</div>
          <div className="ds-error">Error: {err}</div>
          <button className="ds-btn" onClick={() => navigate("/dashboard")}>Back</button>
        </div>
      </div>
    );

  const currency = (n) =>
    Number(n ?? 0).toLocaleString("en-SG", { style: "currency", currency: "SGD", minimumFractionDigits: 2 });

  return (
    <div className="ds-container">
      <div className="ds-card">
        <div className="ds-header">
          <div className="ds-title">Day Cashflow (Totals)</div>
          <div className="ds-date-chip">DATE: {data?.date}</div>
        </div>

        <div className="ds-panels">
          <div className="ds-panel">
            <div className="ds-panel-title">Grant (Amount out)</div>
            <div className="ds-row">
              <div className="ds-label">New</div>
              <div className="ds-value">{data?.grantNewCount ?? 0}</div>
              <div className="ds-amt">{currency(data?.grantNewTotal)}</div>
            </div>
            <div className="ds-row">
              <div className="ds-label">Renew</div>
              <div className="ds-value">{data?.grantRenewCount ?? 0}</div>
              <div className="ds-amt">{currency(data?.grantRenewTotal)}</div>
            </div>
            <div className="ds-divider" />
            <div className="ds-row ds-strong">
              <div className="ds-label">Total</div>
              <div className="ds-value">{data?.grantTotalCount ?? 0}</div>
              <div className="ds-amt">{currency(data?.grantTotalAmount)}</div>
            </div>
          </div>

          <div className="ds-panel">
            <div className="ds-panel-title">Redeem (Amount in)</div>
            <div className="ds-row">
              <div className="ds-label">Total Clients</div>
              <div className="ds-value">{data?.redeemCount ?? 0}</div>
              <div className="ds-amt">{currency(data?.redeemTotal)}</div>
            </div>
            <div className="ds-row">
              <div className="ds-label">Interest</div>
              <div className="ds-value">—</div>
              <div className="ds-amt">{currency(data?.redeemInterest)}</div>
            </div>
            <div className="ds-divider" />
            <div className="ds-row ds-strong">
              <div className="ds-label">Balance (in)</div>
              <div className="ds-value">—</div>
              <div className="ds-amt">
                {currency(data?.redeemTotal)} | {currency(data?.redeemGrandTotal)} |{" "}
                {currency((data?.redeemGrandTotal ?? 0) - (data?.redeemTotal ?? 0))}
              </div>
            </div>
          </div>
        </div>

        {/* SINGLE footer at the bottom */}
        <div className="ds-footer">
          <div className="ds-footer-label">Final cash amount</div>
          <div className={`ds-footer-amt ${Number(data?.balance) < 0 ? "neg" : ""}`}>
            {/* {currency(Number(data?.grantTotalAmount) - Number(data?.redeemTotal))} */}
            {currency(Number(data?.balance))}
          </div>
        </div>

        <div className="ds-actions">
          <button className="ds-btn" onClick={() => navigate("/dashboard")}>Back (Esc)</button>
        </div>
      </div>
    </div>
  );
}
